// Authentication API
import { apiRequest, setAuthRefreshToken } from './request.js';
import { getStoredUtm } from '../../utils/utmAttribution';

export const authAPI = {
    // Register new user
    register: async (userData) => {
        // Adjuntamos el UTM capturado al landing (si lo hay) para que el
        // backend lo guarde en User. Si el caller ya pasó utm_*, ganan los
        // suyos — útil para tests o sobreescritura intencional.
        const utm = getStoredUtm();
        const payload = { ...utm, ...userData };
        const response = await apiRequest('/auth/register/', {
            method: 'POST',
            body: JSON.stringify(payload),
            requireAuth: false
        });

        // Flujo actual: el backend responde con verification_required=true y
        // NO manda tokens. No hay auto-login — el user debe confirmar el
        // link del mail y volver a la pantalla de login. Sólo si el backend
        // envía tokens (fallback histórico o cuenta preverificada) guardamos.
        if (response.token || response.access) {
            localStorage.setItem('authToken', response.token || response.access);
            window.dispatchEvent(new CustomEvent('auth-token-change'));
            if (response.refresh) {
                localStorage.setItem('refreshToken', response.refresh);
            }
            localStorage.setItem('currentUser', JSON.stringify(response.user));
        }

        return response;
    },

    // Reenviar link de verificación al mail. Silencioso desde el backend
    // (no revela si el email existe) — el frontend siempre muestra "Si te
    // falta verificar te enviamos un link".
    requestEmailVerification: async (email) => {
        return apiRequest('/auth/verify-email/request/', {
            method: 'POST',
            body: JSON.stringify({ email }),
            requireAuth: false,
        });
    },

    // Confirmar el token del link de verificación. Devuelve { email, message }
    // en éxito y en error viene .code = 'invalid_token' | 'expired_token' | 'used_token'.
    confirmEmailVerification: async (token) => {
        return apiRequest('/auth/verify-email/confirm/', {
            method: 'POST',
            body: JSON.stringify({ token }),
            requireAuth: false,
        });
    },

    // Login user
    login: async (credentials) => {
        const response = await apiRequest('/auth/login/', {
            method: 'POST',
            body: JSON.stringify(credentials),
            requireAuth: false
        });

        // Store tokens on successful login (API returns access, refresh, and token)
        if (response.token || response.access) {
            localStorage.setItem('authToken', response.token || response.access);
            window.dispatchEvent(new CustomEvent('auth-token-change'));
            if (response.refresh) {
                localStorage.setItem('refreshToken', response.refresh);
            }
            localStorage.setItem('currentUser', JSON.stringify(response.user));
        }

        return response;
    },

    // Get current user profile
    getProfile: async () => {
        return apiRequest('/auth/me/');
    },

    // Refresh token
    refreshToken: async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await apiRequest('/auth/refresh/', {
            method: 'POST',
            body: JSON.stringify({ refresh: refreshToken }),
            requireAuth: false
        });

        // El backend puede devolver el nuevo access token como 'token' o como 'access'
        // (SimpleJWT estándar usa 'access'). Hay que manejar ambos.
        const newAccessToken = response.token || response.access;
        if (newAccessToken) {
            localStorage.setItem('authToken', newAccessToken);
            // 'silent' = true → refresh automático, no login/logout.
            // DataContext no borra empresas en este caso.
            window.dispatchEvent(new CustomEvent('auth-token-change', { detail: { silent: true } }));
        }
        // Store rotated refresh token if backend returns one
        if (response.refresh) {
            localStorage.setItem('refreshToken', response.refresh);
        }

        return response;
    },

    // LinkedIn OAuth login
    loginWithLinkedIn: async (code) => {
        // Send redirect_uri so backend uses the SAME one when exchanging code with LinkedIn
        const redirectUri = `${window.location.origin}/auth/linkedin/callback`;
        const payload = { code, redirect_uri: redirectUri };
        // Backend always creates users as 'professional' - no role parameter needed
        const response = await apiRequest('/auth/linkedin/', {
            method: 'POST',
            body: JSON.stringify(payload),
            requireAuth: false
        });

        if (response.access) {
            localStorage.setItem('authToken', response.access);
            window.dispatchEvent(new CustomEvent('auth-token-change'));
            if (response.refresh) {
                localStorage.setItem('refreshToken', response.refresh);
            }
            // Merge profile data into user object for complete localStorage snapshot
            const userWithProfile = {
                ...response.user,
                profile: response.profile || {},
                avatar_url: response.profile?.avatar_url || response.user?.picture || '',
                linkedin_headline: response.profile?.linkedin_headline || '',
                linkedin_profile_url: response.profile?.linkedin_profile_url || '',
                is_linkedin_connected: true,
            };
            localStorage.setItem('currentUser', JSON.stringify(userWithProfile));
        }

        return response;
    },

    // Get LinkedIn OAuth URL
    getLinkedInAuthUrl: () => {
        const clientId = '77wsz6z05qy1wo';
        // Dynamic redirect URI based on current origin (localhost vs production)
        const redirectUri = encodeURIComponent(`${window.location.origin}/auth/linkedin/callback`);
        const scope = encodeURIComponent('openid profile email');
        // Generate secure random state (crypto-based, 32 chars)
        const state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
        sessionStorage.setItem('linkedin_oauth_state', state);
        sessionStorage.setItem('linkedin_oauth_mode', 'login');
        console.log('[LinkedIn OAuth] Generated state:', state, 'Redirect URI:', window.location.origin + '/auth/linkedin/callback');
        return `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;
    },

    // Get LinkedIn OAuth URL para vincular (no login) — el callback llama a /auth/linkedin/link/
    getLinkedInLinkUrl: () => {
        const clientId = '77wsz6z05qy1wo';
        const redirectUri = encodeURIComponent(`${window.location.origin}/auth/linkedin/callback`);
        const scope = encodeURIComponent('openid profile email');
        const state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');
        sessionStorage.setItem('linkedin_oauth_state', state);
        sessionStorage.setItem('linkedin_oauth_mode', 'link');
        return `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;
    },

    // Vincular LinkedIn al usuario autenticado (no emite JWT nuevo)
    linkLinkedInAccount: async (code) => {
        const redirectUri = `${window.location.origin}/auth/linkedin/callback`;
        return apiRequest('/auth/linkedin/link/', {
            method: 'POST',
            body: JSON.stringify({ code, redirect_uri: redirectUri }),
            requireAuth: true,
        });
    },

    // Request password reset
    requestPasswordReset: async (email) => {
        return apiRequest('/auth/password-reset/', {
            method: 'POST',
            body: JSON.stringify({ email }),
            requireAuth: false
        });
    },

    // Confirm password reset with token
    confirmPasswordReset: async ({ token, password, password_confirm }) => {
        return apiRequest('/auth/password-reset/confirm/', {
            method: 'POST',
            body: JSON.stringify({ token, password, password_confirm }),
            requireAuth: false
        });
    },

    // Deactivate account (soft delete)
    deactivateAccount: async (password = null) => {
        const refreshToken = localStorage.getItem('refreshToken');
        const body = { refresh: refreshToken };
        if (password) body.password = password;
        const response = await apiRequest('/auth/deactivate/', {
            method: 'POST',
            body: JSON.stringify(body)
        });
        // Clear local storage after deactivation
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('currentUser');
        window.dispatchEvent(new CustomEvent('auth-token-change'));
        return response;
    },

    // Logout
    logout: async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            await apiRequest('/auth/logout/', {
                method: 'POST',
                body: JSON.stringify({ refresh: refreshToken })
            });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear local storage regardless of API call success
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('currentUser');
            window.dispatchEvent(new CustomEvent('auth-token-change'));
        }
    }
};

// Register refreshToken for request module
setAuthRefreshToken(authAPI.refreshToken);
